import React, { useEffect, useState, useCallback } from "react";
import styles from "./ForecastSection.module.css";
import { fetchDailyForecast } from "../../../services/api/forecast";
import { useForecastStore } from "../../../store/forecastStore";
import Preloader from "../../../components/common/Preloader";
import StepError from "../../../components/common/StepError";
import Button from "../../../components/common/Button";
import { useShareForecast } from "../../../hooks/useShareForecast";

const MAX_PREVIEW_LENGTH = 600;

const ForecastDay: React.FC = () => {
  const getLocalISODate = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };
  const today = getLocalISODate();
  const { dailyCache, setDailyForecast } = useForecastStore();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forecast, setForecast] = useState<string>("");
  const [isFullVisible, setIsFullVisible] = useState(false);

  const { showCopyModal, setShowCopyModal, toast, textAreaRef, shareForecast, manualCopy } =
    useShareForecast();

  const shareText = `Прогноз на сегодня:\n\n${forecast}`;
  const isLongForecast = forecast.length > MAX_PREVIEW_LENGTH;

  const loadForecast = useCallback(async () => {
    if (dailyCache?.date === today) {
      setForecast(dailyCache.forecast);
      return;
    }
    setLoading(true);
    setError(null);
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    try {
      const data = await fetchDailyForecast(today, timezone);
      setForecast(data.forecast ?? "");
      setDailyForecast(today, data.forecast ?? "");
    } catch (err: unknown) {
      const error = err as {
        response?: { data?: { message?: string; error?: string } };
        message?: string;
      };
      const message =
        error?.response?.data?.error || error?.response?.data?.message || error?.message || "";

      if (
        typeof message === "string" &&
        (message.includes("available in a few hours") ||
          message.includes("requires a subscription"))
      ) {
        setError("Прогноз будет доступен через несколько часов. Пожалуйста, попробуйте позже.");
      } else {
        setError("Не удалось загрузить прогноз на сегодня. Попробуйте снова.");
      }
    } finally {
      setLoading(false);
    }
  }, [dailyCache, setDailyForecast, today]);

  useEffect(() => {
    loadForecast();
  }, [loadForecast]);

  if (loading)
    return (
      <div style={{ marginTop: "-150px" }}>
        <Preloader width={220} height={220} />
      </div>
    );
  if (error) return <StepError message={error} onRetry={loadForecast} showRetry />;

  return (
    <>
      <div className={styles.section}>
        <div className={styles.titleBlock}>
          <h3 className={styles.forecastTitle}>Прогноз на сегодня</h3>
          <a
            href="#"
            className={styles.share}
            onClick={(e) => {
              e.preventDefault();
              shareForecast(shareText);
            }}
          >
            <svg width={28} height={28} viewBox="0 0 28 28" fill="none">
              <rect x="5" y="12" width="18" height="10" rx="4" stroke="#fff" strokeWidth="1.2" />
              <path d="M14 18V5" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
              <path
                d="M10 9l4-4 4 4"
                stroke="#fff"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
        </div>
        <p className={styles.forecastText}>
          {isFullVisible || !isLongForecast
            ? forecast
            : forecast.slice(0, MAX_PREVIEW_LENGTH) + "..."}
        </p>
        {isLongForecast && (
          <div className={styles.toggleButtonWrapper}>
            <Button onClick={() => setIsFullVisible(!isFullVisible)}>
              {isFullVisible ? "Скрыть прогноз" : "Показать полностью"}
            </Button>
          </div>
        )}
      </div>
      {toast && <div className={styles.toast}>{toast}</div>}
      {showCopyModal && (
        <div className={styles.copyModal}>
          <div className={styles.copyModalContent}>
            <textarea
              ref={textAreaRef}
              value={shareText}
              readOnly
              style={{ width: "90%", minHeight: 120, fontSize: 14 }}
              onFocus={(e) => e.target.select()}
            />
            <div style={{ marginTop: 12, display: "flex", justifyContent: "center", gap: 12 }}>
              <Button onClick={manualCopy}>Копировать</Button>
              <Button onClick={() => setShowCopyModal(false)}>Закрыть</Button>
            </div>
            <div className={styles.copyHint}>
              Если копирование не сработало, выделите текст вручную.
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ForecastDay;
